import { applyMutation, mergeRecord } from '@nedelnik/shared';

import { recordKey } from './schema.js';

import type { LocalDb, NumberLease, QuarantinedMutation, StoredRecord } from './schema.js';
import type { Entity, Mutation, SyncRecord } from '@nedelnik/shared';

/**
 * Операции над локальной копией: слияние входящего, применение своей правки,
 * очередь отправки, карантин и мета.
 *
 * Оба пути записи — своя правка и чужая — сходятся в `mergeRecord` из `shared`,
 * то есть в тот же код, которым сливает сервер. Второй реализации слияния на
 * клиенте нет и быть не должно: она разъедется с первой, и конвергенция,
 * ради которой затеян весь протокол, перестанет выполняться.
 */

export interface MergeResult {
  entity: Entity;
  key: string;
  record: SyncRecord;
}

export async function readAllRecords(db: LocalDb): Promise<StoredRecord[]> {
  return db.getAll('records');
}

export async function readRecord(
  db: LocalDb,
  entity: Entity,
  key: string,
): Promise<SyncRecord | undefined> {
  const stored = await db.get('records', [entity, key]);
  return stored?.record;
}

/** Слить входящую запись с локальной. Идемпотентно: повторная выдача ничего не портит. */
export async function mergeIncoming(
  db: LocalDb,
  entity: Entity,
  incoming: SyncRecord,
): Promise<MergeResult> {
  const key = recordKey(entity, incoming);
  const tx = db.transaction('records', 'readwrite');
  const local = (await tx.store.get([entity, key]))?.record;
  const record = mergeRecord(local, incoming);

  await tx.store.put({ entity, key, record });
  await tx.done;

  return { entity, key, record };
}

/**
 * Применить свою правку: сначала к локальной копии, потом в очередь. Порядок
 * важен — интерфейс обязан показать правку немедленно, не дожидаясь сети
 * (принцип «нет кнопки Сохранить», раздел 5.3 архитектуры).
 */
export async function applyLocalMutation(
  db: LocalDb,
  mutation: Mutation,
  seed?: Partial<SyncRecord>,
): Promise<MergeResult> {
  const key = mutation.entity === 'settings' ? String(seed?.['user_id'] ?? '') : mutation.entity_id;

  const tx = db.transaction(['records', 'queue'], 'readwrite');
  const records = tx.objectStore('records');

  const local = (await records.get([mutation.entity, key]))?.record;
  const record = applyMutation(local, mutation, seed);

  await records.put({ entity: mutation.entity, key, record });
  await tx.objectStore('queue').add({ mutation });
  await tx.done;

  return { entity: mutation.entity, key, record };
}

// --------------------------------------------------------------------------
// Очередь
// --------------------------------------------------------------------------

export interface QueueBatch {
  ops: Mutation[];
  /** Ключи очереди, которые батч занял: по ним он и вычёркивается. */
  keys: number[];
}

/**
 * Взять пачку из очереди. Ограничена и числом мутаций, и объёмом: сервер
 * отвергает пачку больше мегабайта целиком, и узнать об этом после отправки —
 * значит потерять раунд на каждой большой правке.
 */
export async function takeBatch(
  db: LocalDb,
  limits: { maxOps: number; maxBytes: number },
): Promise<QueueBatch> {
  const ops: Mutation[] = [];
  const keys: number[] = [];
  let bytes = 0;

  let cursor = await db.transaction('queue').store.openCursor();
  while (cursor !== null && ops.length < limits.maxOps) {
    const size = new TextEncoder().encode(JSON.stringify(cursor.value.mutation)).length;
    if (ops.length > 0 && bytes + size > limits.maxBytes) break;

    ops.push(cursor.value.mutation);
    keys.push(cursor.key);
    bytes += size;
    cursor = await cursor.continue();
  }

  return { ops, keys };
}

export async function dropFromQueue(db: LocalDb, keys: number[]): Promise<void> {
  const tx = db.transaction('queue', 'readwrite');
  for (const key of keys) {
    await tx.store.delete(key);
  }
  await tx.done;
}

export async function queueLength(db: LocalDb): Promise<number> {
  return db.count('queue');
}

/**
 * Откатить отвергнутую правку по полям: снять часы с тех полей, которые она
 * трогала.
 *
 * Зачем не удалять запись целиком. Отказ по одному полю — скажем, срок не
 * прошёл проверку типа — уносил с экрана всю задачу вместе с названием,
 * чек-листом и тегами, и возвращал её только снимок. Один неверный символ в
 * дате стоил исчезнувшей задачи.
 *
 * Почему именно часы, а не значения. Слияние сравнивает часы, а не содержимое:
 * пока у поля стоят часы нашей отвергнутой правки, они старше серверных, и
 * снимок **не перепишет** его — сервер проиграет по LWW и правда не вернётся.
 * Сняв часы, мы возвращаем полю состояние «мы про него ничего не знаем», и
 * ближайший снимок кладёт туда серверное значение со своими часами.
 *
 * Что остаётся неидеальным и почему это терпимо: если поля не было и на
 * сервере, снимок про него ничего не скажет, и наше отвергнутое значение
 * останется лежать без часов. Оно не разъедется по устройствам — без часов его
 * никто не примет, — и вылечится первой же успешной правкой того же поля.
 */
export async function rollbackFields(
  db: LocalDb,
  entity: Entity,
  key: string,
  fields: readonly string[],
): Promise<MergeResult | undefined> {
  const tx = db.transaction('records', 'readwrite');
  const stored = await tx.store.get([entity, key]);

  if (stored === undefined) {
    await tx.done;
    return undefined;
  }

  const clocks = { ...stored.record.field_clocks };
  for (const field of fields) {
    delete clocks[field];
  }

  const record = { ...stored.record, field_clocks: clocks };
  await tx.store.put({ entity, key, record });
  await tx.done;

  return { entity, key, record };
}

/**
 * Убрать записи из локальной копии — насовсем, без надгробия.
 *
 * Это не удаление задачи, а откат: сервер отверг правку, значит такой записи у
 * него нет или она другая, и держать свою версию нельзя. Надгробие тут было бы
 * ошибкой: оно разъехалось бы по устройствам как настоящее удаление.
 */
export async function purgeRecords(
  db: LocalDb,
  keys: { entity: Entity; key: string }[],
): Promise<void> {
  const tx = db.transaction('records', 'readwrite');
  for (const item of keys) {
    await tx.store.delete([item.entity, item.key]);
  }
  await tx.done;
}

// --------------------------------------------------------------------------
// Карантин
// --------------------------------------------------------------------------

export async function quarantine(
  db: LocalDb,
  entries: { mutation: Mutation; reason: string; at: string }[],
): Promise<void> {
  const tx = db.transaction('quarantine', 'readwrite');
  for (const entry of entries) {
    await tx.store.put({
      op_id: entry.mutation.op_id,
      mutation: entry.mutation,
      reason: entry.reason,
      at: entry.at,
    });
  }
  await tx.done;
}

export async function readQuarantine(db: LocalDb): Promise<QuarantinedMutation[]> {
  return db.getAll('quarantine');
}

/**
 * Выбросить отвергнутые правки.
 *
 * Восстановить их нечем: запись в локальной копии уже откатилась, когда сервер
 * отказался её принять, так что в карантине лежит только сама мутация. Поэтому
 * действие необратимое, и спрашивать о нём должен интерфейс, а не эта функция.
 */
export async function forgetQuarantine(db: LocalDb): Promise<void> {
  const tx = db.transaction('quarantine', 'readwrite');
  await tx.store.clear();
  await tx.done;
}

// --------------------------------------------------------------------------
// Мета
// --------------------------------------------------------------------------

export const META_CURSOR = 'cursor';
export const META_NUMBERS = 'numbers';
export const META_CLOCK = 'clock';
export const META_DEVICE = 'device_id';
/**
 * Кто вошёл. Хранится локально не ради удобства: без этого запуск без сети
 * показывает экран входа поверх целой локальной копии — то есть приложение,
 * которое держит все данные у себя, отказывается их показать, потому что не
 * дозвонилось до сервера.
 */
export const META_IDENTITY = 'identity';
/**
 * Чьи задачи лежат в этой копии.
 *
 * Метка отдельная от `META_IDENTITY`, и различие принципиальное: «кто вошёл»
 * пропадает, как только сервер ответил «сессии нет» — отключили устройство,
 * истёк refresh, — а копия при этом никуда не делась и остаётся чужой. Пока
 * владелец хранился той же меткой, вход другого человека на этом устройстве
 * не заставал никакого предыдущего: сравнивать было уже не с чем, и копия
 * доставалась ему вместе с курсором прежнего хозяина (3.16.1).
 *
 * Снимается эта метка только вместе с самой копией — в `clearLocal`.
 */
export const META_OWNER = 'copy_owner';

/**
 * Взять следующий номер задачи — одной транзакцией над базой, а не из копии
 * блока в памяти.
 *
 * Копия в памяти у каждой вкладки своя, а база одна на origin: две вкладки
 * читали один и тот же `next` и выдавали один номер дважды. Сервер такую
 * вторую задачу отвергает как дубль, и правка молча оседает в карантине —
 * ровно это и случилось при проверке этапа 9 с двумя вкладками.
 */
export async function takeNumber(db: LocalDb): Promise<number | undefined> {
  const tx = db.transaction('meta', 'readwrite');
  const lease = (await tx.store.get(META_NUMBERS)) as NumberLease | undefined;

  if (lease === undefined || lease.next > lease.to) {
    await tx.done;
    return undefined;
  }

  await tx.store.put({ next: lease.next + 1, to: lease.to }, META_NUMBERS);
  await tx.done;
  return lease.next;
}

export async function readMeta<T>(db: LocalDb, key: string): Promise<T | undefined> {
  return (await db.get('meta', key)) as T | undefined;
}

export async function writeMeta(db: LocalDb, key: string, value: unknown): Promise<void> {
  await db.put('meta', value, key);
}

/** Полная очистка локальной копии: выход из учётной записи и смена пользователя. */
export async function clearLocal(db: LocalDb): Promise<void> {
  const tx = db.transaction(['records', 'queue', 'quarantine', 'meta'], 'readwrite');
  await Promise.all([
    tx.objectStore('records').clear(),
    tx.objectStore('queue').clear(),
    tx.objectStore('quarantine').clear(),
    tx.objectStore('meta').clear(),
  ]);
  await tx.done;
}
